import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createProject, getProjectInformation } from '../../api';
import {
  NORMAL_PRICE,
  TEXT_STRING,
  queryKeys,
  routes,
  storageKeys,
} from '../../constants';
import { useOrderStore, useSettingStore, useUserStore } from '../../store';
import SummaryOrder from '../../components/OrderScreen/SummaryOrder';
import CallWoman, {
  CallWomanRef,
} from '../../components/OrderScreen/CallWoman';
import OrderDetail from '../../components/OrderScreen/OrderDetail';
import {
  EOrderStep,
  EProjectType,
  TCreateProject,
  TFemale,
  TMaleDetail,
} from '../../types';
import {
  Header,
  PriceList,
  Text,
  LineLoginModalContent,
  Modal,
  LineAddFriendModalContent,
} from '../../components';
import moment from 'moment';
import { FIELD_NAME, initialValueCallWoman } from '../../helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalSuccess from '../../components/ModalSuccess';
import { toast } from 'react-toastify';
import { detectPWA, getFormattedAmount } from 'utils';
import { useAuth, useLine } from 'hooks';

type InvitedLocationState = {
  nominatedFemale?: TFemale;
  invitedMales?: TMaleDetail[];
  invitedMale?: TMaleDetail;
  sourceChannelId?: string;
} | null;

function invitedRosterFromLocationState(
  st: InvitedLocationState,
): TMaleDetail[] {
  if (st?.invitedMales?.length) return st.invitedMales;
  if (st?.invitedMale) return [st.invitedMale];
  return [];
}

const titleModal = {
  [EOrderStep.ORDER_METHOD]: TEXT_STRING.COMMON.MENU,
  [EOrderStep.CALL_WOMAN]: TEXT_STRING.SUMMARY_ORDER.ORDER,
  [EOrderStep.ORDER_DETAIL]: TEXT_STRING.ORDER_DETAIL.RECRUITMENT_DETAIL,
  [EOrderStep.SUMMARY_ORDER]: TEXT_STRING.SUMMARY_ORDER.CONFIRM_ORDER,
};

function Call() {
  // State
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [salonProjectId, setSalonProjectId] = useState<number | null>(null);
  const callWomanRef = useRef<CallWomanRef>(null);

  // Hooks
  const {
    listSelectedGirl,
    setListSelectedGirl,
    setOrderStep,
    orderStep,
    orderFormState: formState,
    setOrderFormState: setFormState,
    fixedFemaleId,
    sourceChannelId: settingSourceChannelId,
    clearSettingOrderMeta,
  } = useOrderStore();
  const { user } = useUserStore();

  const navigate = useNavigate();
  const { setOpenTutorial } = useSettingStore();
  const queryClient = useQueryClient();
  const { checkNeedToLogin } = useAuth();
  const { lineGate, ensureLineGate, clearLineGate, loginOrRedirectLineOAuth } =
    useLine();
  const location = useLocation();
  const nominatedFemale: TFemale | undefined = location.state?.nominatedFemale;
  const sourceChannelId: string | undefined = (
    location.state as InvitedLocationState
  )?.sourceChannelId;
  const [invitedMaleRoster, setInvitedMaleRoster] = useState<TMaleDetail[]>(
    () =>
      invitedRosterFromLocationState(location.state as InvitedLocationState),
  );
  const [activeInvitedMaleIds, setActiveInvitedMaleIds] = useState<number[]>(
    () =>
      invitedRosterFromLocationState(
        location.state as InvitedLocationState,
      ).map((m) => m.id),
  );

  const toggleInvitedMale = useCallback((maleId: number) => {
    setActiveInvitedMaleIds((prev) => {
      if (prev.includes(maleId)) {
        if (prev.length <= 1) return prev;
        return prev.filter((id) => id !== maleId);
      }
      return [...prev, maleId];
    });
  }, []);

  // Queries, mutations
  const { data } = useQuery({
    queryKey: [queryKeys.PROJECT_INFO],
    queryFn: getProjectInformation,
  });

  const { mutateAsync: createOrderMutation, data: project } = useMutation({
    mutationFn: (body: TCreateProject) => {
      return createProject(body);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  // Memo, callbacks
  const totalPriceGirl = useMemo(() => {
    const setCount = +formState[FIELD_NAME.SET];
    const peopleCount = +formState[FIELD_NAME.PEOPLE];
    const girlCount = peopleCount - listSelectedGirl.length;
    let total = 0;
    total =
      listSelectedGirl.reduce((result, current) => {
        return result + NORMAL_PRICE * setCount + current.nominationFee;
      }, 0) +
      girlCount * NORMAL_PRICE * setCount;

    return getFormattedAmount(total);
  }, [formState, listSelectedGirl]);

  const salonDerbyUrl = useMemo(() => {
    const baseUrl = process.env.REACT_APP_SALON_BASE_URL;
    if (
      !openModal ||
      user?.isSalonMember !== true ||
      !Number.isSafeInteger(salonProjectId) ||
      (salonProjectId ?? 0) <= 0 ||
      !baseUrl
    ) {
      return null;
    }

    try {
      const base = new URL(baseUrl);
      if (base.protocol !== 'https:') return null;
      const url = new URL('/derby/free', base.origin);
      url.searchParams.set('projectId', String(salonProjectId));
      return url.toString();
    } catch {
      return null;
    }
  }, [openModal, salonProjectId, user?.isSalonMember]);

  const clearState = useCallback(() => {
    const st = location.state as InvitedLocationState;
    if (
      st?.nominatedFemale ||
      st?.invitedMale ||
      (st?.invitedMales && st.invitedMales.length > 0)
    ) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    setFormState(initialValueCallWoman);
    setListSelectedGirl([]);
    setInvitedMaleRoster([]);
    setActiveInvitedMaleIds([]);
  }, [
    location.pathname,
    location.state,
    navigate,
    setFormState,
    setListSelectedGirl,
  ]);

  const executeOrderCreation = useCallback(async () => {
    const combineDateTime = () => {
      const date = moment(formState[FIELD_NAME.DATE]);
      const time = moment(formState[FIELD_NAME.TIME], 'HH:mm');
      const dateTime = date.set({
        hour: time.hour(),
        minute: time.minute(),
      });

      return dateTime.toISOString();
    };

    const projectType =
      activeInvitedMaleIds.length > 0
        ? EProjectType.SALON
        : formState[FIELD_NAME.ORDER_TYPE];

    const data = {
      meetingAddress: `${formState[FIELD_NAME.MEETING_ADDRESS]}`.trim(),
      note: `${formState[FIELD_NAME.NOTE]}`.trim(),
      setCount: +formState[FIELD_NAME.SET],
      peopleCount: +formState[FIELD_NAME.PEOPLE],
      startDate: combineDateTime(),
      maleCount: +formState[FIELD_NAME.MALE_COUNT],
      ProjectCandidate: listSelectedGirl.map((girl) => girl.id),
      estimatePrice: totalPriceGirl,
      type: projectType,
    };
    const areaId = +formState[FIELD_NAME.AREA_ID];
    try {
      setLoading(true);
      setSalonProjectId(null);
      const payload = {
        ...data,
        ...(activeInvitedMaleIds.length
          ? { invitedMaleIds: activeInvitedMaleIds }
          : {}),
        ...(areaId ? { areaId } : {}),
        ...(sourceChannelId && activeInvitedMaleIds.length
          ? { channelId: sourceChannelId }
          : {}),
        ...(fixedFemaleId ? { settingFemaleId: fixedFemaleId } : {}),
      };
      const createdProject = await createOrderMutation(payload);
      const hasFreeSlot =
        Number.isInteger(payload.peopleCount) &&
        payload.peopleCount - new Set(payload.ProjectCandidate).size > 0;
      setSalonProjectId(hasFreeSlot ? createdProject?.id ?? null : null);
      setOrderStep(EOrderStep.ORDER_METHOD);
      setOpenModal(true);
      clearState();
      clearSettingOrderMeta();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [
    clearState,
    createOrderMutation,
    formState,
    listSelectedGirl,
    setOrderStep,
    activeInvitedMaleIds,
    totalPriceGirl,
    sourceChannelId,
    fixedFemaleId,
    clearSettingOrderMeta,
  ]);

  const handleOrderGirl = useCallback(async () => {
    await executeOrderCreation();
  }, [executeOrderCreation]);

  const handleNextStep = useCallback(async () => {
    if (orderStep === EOrderStep.ORDER_METHOD) {
      const isLineGate = await ensureLineGate();
      if (!isLineGate) return;
    }

    if (orderStep === EOrderStep.SUMMARY_ORDER) {
      await handleOrderGirl();
      return;
    }

    if (orderStep === EOrderStep.CALL_WOMAN) {
      const isValid = callWomanRef.current?.handleValidate();
      if (!isValid) return;
    }

    setOrderStep(orderStep + 1);
  }, [orderStep, ensureLineGate, handleOrderGirl, setOrderStep]);

  const handlePreviousStep = useCallback(
    (step?: number) => {
      if (!orderStep) return;
      if (orderStep === EOrderStep.ORDER_METHOD) {
        clearSettingOrderMeta();
        navigate(-1);
      } else {
        if (!!invitedMaleRoster.length && orderStep === EOrderStep.CALL_WOMAN) {
          clearSettingOrderMeta();
          navigate(-1);
          return;
        }
        if (orderStep === EOrderStep.CALL_WOMAN && settingSourceChannelId) {
          clearSettingOrderMeta();
          navigate(routes.CONVERSATION.replace(':id', settingSourceChannelId), {
            state: {
              isFinish: true,
            },
          });

          return;
        }
        setOrderStep(step || orderStep - 1);
      }
    },
    [
      navigate,
      orderStep,
      setOrderStep,
      invitedMaleRoster.length,
      settingSourceChannelId,
      clearSettingOrderMeta,
    ],
  );

  // Effect
  useEffect(() => {
    if (nominatedFemale) {
      clearState();
      setListSelectedGirl([nominatedFemale]);
    }
  }, [clearState, nominatedFemale, setListSelectedGirl]);

  useEffect(() => {
    const next = invitedRosterFromLocationState(
      location.state as InvitedLocationState,
    );
    setInvitedMaleRoster(next);
    setActiveInvitedMaleIds(next.map((m) => m.id));
    if (!next.length) return;
    setFormState((prev) => {
      const need = next.length + 1;
      return {
        ...prev,
        [FIELD_NAME.MALE_COUNT]:
          Number(prev[FIELD_NAME.MALE_COUNT] || 0) < need
            ? String(need)
            : prev[FIELD_NAME.MALE_COUNT],
      };
    });
  }, [location.state, setFormState]);
  return (
    <>
      <div className="h-full flex flex-col">
        <Header
          title={orderStep ? titleModal[orderStep] : ''}
          onBack={() =>
            handlePreviousStep(
              orderStep === EOrderStep.ORDER_DETAIL
                ? EOrderStep.CALL_WOMAN
                : undefined,
            )
          }
        />
        <Modal
          open={lineGate === 'LINE' || lineGate === 'LINE_FRIEND'}
          onClose={clearLineGate}
          className="px-5 py-6 mx-5 bg-white w-full flex justify-center items-center"
        >
          {lineGate === 'LINE' && (
            <LineLoginModalContent
              onCancel={clearLineGate}
              onClick={loginOrRedirectLineOAuth}
              loading={false}
              disabled={false}
            />
          )}

          {lineGate === 'LINE_FRIEND' && (
            <LineAddFriendModalContent
              onSuccess={clearLineGate}
              onClose={clearLineGate}
            />
          )}
        </Modal>
        {orderStep === EOrderStep.ORDER_METHOD && (
          <PriceList onNext={handleNextStep} />
        )}
        {orderStep === EOrderStep.CALL_WOMAN && (
          <div className="h-full p-5 flex flex-col justify-between gap-4 overflow-y-auto">
            <CallWoman
              ref={callWomanRef}
              projectInfo={data}
              formState={formState}
              setFormState={setFormState}
              listSelectedGirl={listSelectedGirl}
              setListSelectedGirl={setListSelectedGirl}
              invitedMaleRoster={invitedMaleRoster}
              activeInvitedMaleIds={activeInvitedMaleIds}
              onToggleInvitedMale={toggleInvitedMale}
              estimatePrice={totalPriceGirl}
              loading={loading}
              onOrder={handleNextStep}
              fixUserId={fixedFemaleId ?? undefined}
            />
          </div>
        )}
        {orderStep === EOrderStep.ORDER_DETAIL && (
          <OrderDetail
            projectInfo={data}
            formState={formState}
            setFormState={setFormState}
            onSubmitOrder={() => {
              if (!checkNeedToLogin(routes.SIGNUP)) handleNextStep();
            }}
            loading={
              !!queryClient.isFetching({ queryKey: [queryKeys.CURRENT_USER] })
            }
          />
        )}
        {orderStep === EOrderStep.SUMMARY_ORDER && (
          <SummaryOrder
            summaryOrder={{
              ...formState,
            }}
            estimatePrice={totalPriceGirl}
            loading={loading}
            onOrderGirl={handleOrderGirl}
          />
        )}
      </div>
      <ModalSuccess
        open={openModal}
        onConfirm={() => {
          setOpenModal(false);
          setSalonProjectId(null);
          navigate(
            routes.CONVERSATION.replace(':id', project?.channelId || ''),
          );
          setOpenTutorial(
            !localStorage.getItem(storageKeys.IS_CONFIRM_TUTORIAL) &&
              !detectPWA(),
            'order_complete',
          );
        }}
      >
        <Text className="mt-6" center textColor="text-neutral-900">
          {TEXT_STRING.SUMMARY_ORDER.ORDER_SUCCESS_MESSAGE} <br />
          {TEXT_STRING.SUMMARY_ORDER.ORDER_SUCCESS_HINT}
        </Text>
        {salonDerbyUrl && (
          <>
            <p className="mt-4 w-full rounded-[8px] bg-primary-50 px-12px py-10px text-center font-bold text-primary-700">
              {TEXT_STRING.SUMMARY_ORDER.SALON_DERBY_FREE_MESSAGE}
            </p>
            <a
              className="mt-10px flex w-full items-center justify-center gap-6px rounded-[8px] border-2 border-primary-700 bg-white px-16px py-14px font-bold text-primary-700 no-underline focus-visible:outline focus-visible:outline-2"
              href={salonDerbyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {TEXT_STRING.SUMMARY_ORDER.SALON_DERBY_FREE_LINK}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 4h6v6" />
                <path d="M20 4l-9 9" />
                <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
              </svg>
            </a>
          </>
        )}
      </ModalSuccess>
    </>
  );
}

export default Call;
